import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { 
  Zap, 
  Video, 
  MessageSquare, 
  User, 
  Trophy, 
  Sparkles, 
  ChevronRight, 
  ChevronLeft,
  X,
  GraduationCap,
  Target
} from "lucide-react";

interface TourStep {
  title: string;
  description: string;
  icon: React.ReactNode;
  highlight?: string;
}

const tourSteps: TourStep[] = [
  {
    title: "Welcome to SkillSwipe! 🎉",
    description: "Ready to learn something new? Let's take a quick tour of your skill-sharing journey!",
    icon: <Sparkles className="h-12 w-12 text-primary" />,
  },
  {
    title: "Find Your Match",
    description: "Swipe through users who match your skills. If you can teach what they want to learn (or vice versa), you'll see a 'Match!' badge. Tap to connect!",
    icon: <Zap className="h-12 w-12 text-orange-500" />,
    highlight: "swipe",
  },
  {
    title: "Showcase Your Skills",
    description: "Post 30-second videos demonstrating your skills. Get 'Impressed' reactions and connect with interested learners!",
    icon: <Video className="h-12 w-12 text-pink-500" />,
    highlight: "showcase",
  },
  {
    title: "Chat & Schedule",
    description: "Message your matches, schedule video sessions, and share resources. Use the calendar icon to book learning sessions directly in chat!",
    icon: <MessageSquare className="h-12 w-12 text-blue-500" />,
    highlight: "chat",
  },
  {
    title: "Build Your Profile",
    description: "Add skills you can teach and want to learn. The more skills you add, the more matches you'll find!",
    icon: <User className="h-12 w-12 text-green-500" />,
    highlight: "profile",
  },
  {
    title: "Earn Points & Achievements",
    description: "Complete sessions, get rated, and unlock achievements to build your SkillSwipe Score. Higher scores = more visibility!",
    icon: <Trophy className="h-12 w-12 text-amber-500" />,
  },
  {
    title: "Pro Tips 💡",
    description: "• Add at least 3 teaching skills and 3 learning goals\n• Complete your profile with a photo\n• Be active to appear in 'Recently Active' badges\n• Rate your partners after sessions",
    icon: <Target className="h-12 w-12 text-purple-500" />,
  },
  {
    title: "You're All Set! 🚀",
    description: "Start swiping, connect with learners, and grow your skills. The learning adventure begins now!",
    icon: <GraduationCap className="h-12 w-12 text-primary" />,
  },
];

interface OnboardingTourProps {
  onComplete: () => void;
  isVisible: boolean;
}

export const OnboardingTour = ({ onComplete, isVisible }: OnboardingTourProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  if (!isVisible) return null;

  const handleNext = () => {
    if (currentStep < tourSteps.length - 1) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep(currentStep + 1);
        setIsAnimating(false);
      }, 200);
    } else {
      onComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep(currentStep - 1);
        setIsAnimating(false);
      }, 200);
    }
  };

  const handleSkip = () => {
    onComplete();
  };

  const step = tourSteps[currentStep];
  const isLastStep = currentStep === tourSteps.length - 1;
  const isFirstStep = currentStep === 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <Card className={`max-w-sm w-full p-6 shadow-strong transition-all duration-200 ${isAnimating ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}>
        {/* Skip button */}
        <div className="flex justify-end mb-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleSkip}
            className="text-muted-foreground hover:text-foreground -mt-2 -mr-2"
          >
            <X className="h-4 w-4 mr-1" />
            Skip
          </Button>
        </div>

        {/* Step indicator */}
        <div className="flex justify-center gap-1.5 mb-6">
          {tourSteps.map((_, index) => (
            <div
              key={index}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                index === currentStep 
                  ? 'w-6 bg-primary' 
                  : index < currentStep 
                    ? 'w-1.5 bg-primary/50' 
                    : 'w-1.5 bg-muted'
              }`}
            />
          ))}
        </div>

        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div className="p-4 rounded-full bg-primary/10">
            {step.icon}
          </div>
        </div>

        {/* Content */}
        <div className="text-center mb-6">
          <h2 className="text-xl font-bold mb-3">{step.title}</h2>
          <p className="text-muted-foreground whitespace-pre-line text-sm leading-relaxed">
            {step.description}
          </p>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between gap-3">
          <Button
            variant="outline"
            onClick={handlePrev}
            disabled={isFirstStep}
            className="flex-1"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          
          <Button
            onClick={handleNext}
            className="flex-1 gradient-primary"
          >
            {isLastStep ? "Let's Go!" : "Next"}
            {!isLastStep && <ChevronRight className="h-4 w-4 ml-1" />}
          </Button>
        </div>

        {/* Step count */}
        <p className="text-center text-xs text-muted-foreground mt-4">
          Step {currentStep + 1} of {tourSteps.length}
        </p>
      </Card>
    </div>
  );
};

// Hook to manage tour state
export const useOnboardingTour = () => {
  const [showTour, setShowTour] = useState(false);
  const TOUR_KEY = 'skillswipe_tour_completed';

  useEffect(() => {
    const tourCompleted = localStorage.getItem(TOUR_KEY);
    if (!tourCompleted) {
      // Small delay to let the app load first
      const timer = setTimeout(() => {
        setShowTour(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const completeTour = () => {
    localStorage.setItem(TOUR_KEY, 'true');
    setShowTour(false);
  };

  const resetTour = () => {
    localStorage.removeItem(TOUR_KEY);
    setShowTour(true);
  };

  return { showTour, completeTour, resetTour };
};

export default OnboardingTour;
